import { useEffect, useState } from "react";
import type { AdminEmailMessagesResponse } from "../models/admin-email.models";
import { getAdminEmailErrorMessage, getAdminEmailMessages } from "../services/admin-email.service";

// Keep Graph page navigation sequential because a later page is known only after its cursor is returned.
// Aborting requests and discarding responses on cleanup prevents mail from a previous page flashing into view.
export const useAdminEmailMessages = (mailboxId: string) => {
  const [response, setResponse] = useState<AdminEmailMessagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = cursors[pageIndex];

  useEffect(() => {
    const controller = new AbortController();
    setResponse(null);
    setLoading(true);
    setError(null);

    getAdminEmailMessages(mailboxId, cursor, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setResponse(result);
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(getAdminEmailErrorMessage(requestError));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [mailboxId, cursor]);

  const previousPage = () => {
    if (!loading && pageIndex > 0) {
      setResponse(null);
      setLoading(true);
      setPageIndex((current) => current - 1);
    }
  };

  const nextPage = () => {
    const nextCursor = response?.pagination.nextCursor;

    if (!loading && response?.pagination.hasNext && nextCursor) {
      setCursors((current) => [...current.slice(0, pageIndex + 1), nextCursor]);
      setResponse(null);
      setLoading(true);
      setPageIndex((current) => current + 1);
    }
  };

  return { response, loading, error, pageIndex, previousPage, nextPage };
};
